import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { TinsmithMark } from "@tinsmith/brand";

import styles from "./AuthGate.module.css";
import { AuthScreen } from "./AuthScreen";
import { useSession } from "./session";

// The routes that render regardless of session, because each carries its own
// credential in the link that reaches it: `/join` is enrollment, where a
// brand-new user has no passkey to sign in with yet, and `/confirm-email` is an
// email-change confirmation, whose link is delivered to the address being
// confirmed and is often opened somewhere this account has never signed in.
const PUBLIC_PATHS: ReadonlySet<string> = new Set(["/confirm-email", "/join"]);

// Gates the app on an authenticated session. While the initial `/me` probe is in
// flight we show a quiet splash; a 401 drops to the passkey sign-in screen;
// success renders the app.
export const AuthGate = ({ children }: { readonly children: ReactNode }) => {
  const { status } = useSession();
  const { pathname } = useLocation();

  if (PUBLIC_PATHS.has(pathname)) {
    return <>{children}</>;
  }

  if (status === "loading") {
    return (
      <div className={styles.splash}>
        <div className={styles.pulse}>
          <TinsmithMark size={40} />
        </div>
      </div>
    );
  }

  if (status === "anonymous") {
    return <AuthScreen />;
  }

  return <>{children}</>;
};
